import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, BookOpen, Ban, BarChart, Settings, Copy, LogOut, Menu, X, Building2, Trophy, Package, ClipboardCheck, Sparkles, GraduationCap, Swords, ClipboardList, UserCheck, Wallet } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import NotificationSetup from './NotificationSetup';

const AdminSidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, facility, logout, isVenueManager, isVenueOwner, isVenueFinance } = useAuth();

  // Define all menu items
  const allMenuItems = [
    { path: '/admin/calendar', icon: Calendar, label: 'Calendar', shortLabel: 'Calendar', allowVenueManager: true, allowVenueOwner: true, allowVenueFinance: true },
    { path: '/admin/bookings', icon: BookOpen, label: 'Bookings', shortLabel: 'Bookings', allowVenueManager: false, allowVenueFinance: true },
    { path: '/admin/bulk-booking', icon: Copy, label: 'Bulk Booking', shortLabel: 'Bulk', allowVenueManager: false },
    { path: '/admin/community-games', icon: Trophy, label: 'Community Games', shortLabel: 'Games', allowVenueManager: false },
    { path: '/admin/events', icon: Sparkles, label: 'Events', shortLabel: 'Events', allowVenueManager: false },
    { path: '/admin/coaching', icon: GraduationCap, label: 'Coaching', shortLabel: 'Coaching', allowVenueManager: false },
    { path: '/admin/tournaments', icon: Swords, label: 'Tournaments', shortLabel: 'Tourneys', allowVenueManager: false },
    { path: '/admin/inventory', icon: Package, label: 'Inventory & POS', shortLabel: 'Inventory', allowVenueManager: true },
    { path: '/admin/operations', icon: ClipboardCheck, label: 'Operations', shortLabel: 'Ops', allowVenueManager: true },
    { path: '/admin/attendance', icon: ClipboardList, label: 'Attendance', shortLabel: 'Attendance', veOnly: true },
    { path: '/admin/staff-attendance', icon: UserCheck, label: 'Attendance', shortLabel: 'Attendance', allowVenueManager: false },
    { path: '/admin/blocks', icon: Ban, label: 'Blocked Slots', shortLabel: 'Blocks', allowVenueManager: false },
    { path: '/admin/reports', icon: BarChart, label: 'Reports', shortLabel: 'Reports', allowVenueManager: false, allowVenueOwner: true },
    { path: '/admin/finance', icon: Wallet, label: 'Finance', shortLabel: 'Finance', allowVenueManager: false, allowVenueFinance: true },
    { path: '/admin/settings', icon: Settings, label: 'Settings', shortLabel: 'Settings', allowVenueManager: false }
  ];

  // Filter menu items based on role.
  // - veOnly items show ONLY for venue managers (their own attendance tab)
  // - venue managers otherwise see only allowVenueManager items
  // - venue_owner / venue_finance (SEC-005 read-only) see only their allowlisted tabs
  // - admin/superadmin see everything except veOnly
  const menuItems = allMenuItems.filter((item) => {
    if (item.veOnly) return isVenueManager;
    if (isVenueOwner) return !!item.allowVenueOwner;
    if (isVenueFinance) return !!item.allowVenueFinance;
    if (isVenueManager) return item.allowVenueManager;
    return true;
  });

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Get sport-specific color
  const getFacilityColor = () => {
    if (!facility?.sports) return 'blue';
    if (facility.sports.includes('padel') || facility.sports.includes('pickle')) {
      return 'emerald';
    }
    return 'blue';
  };

  const color = getFacilityColor();
  const colorClasses = {
    blue: {
      bg: 'bg-blue-100',
      text: 'text-blue-600',
      activeBg: 'bg-blue-50',
      border: 'border-blue-200',
      bottomActive: 'text-blue-600',
      bottomInactive: 'text-gray-500'
    },
    emerald: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-600',
      activeBg: 'bg-emerald-50',
      border: 'border-emerald-200',
      bottomActive: 'text-emerald-600',
      bottomInactive: 'text-gray-500'
    }
  };
  const colors = colorClasses[color];

  return (
    <>
      {/* Mobile Header - Only facility info */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_fieldmaster-24/artifacts/iuxgdyho_Spaces-Logo.png" 
            alt="Spaces" 
            className="h-8"
          />
          {facility?.logo_url && (
            <div className="h-8 px-1.5 flex items-center justify-center bg-gray-900 rounded overflow-hidden">
              <img 
                src={facility.logo_url} 
                alt={facility.name} 
                className="h-6 object-contain"
                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
              />
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t shadow-lg pb-12">
        <div className="flex overflow-x-auto no-scrollbar items-center py-1 px-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} data-testid={`nav-${item.path.split('/').pop()}`} className="flex-shrink-0" style={{ minWidth: '64px' }}>
                <div className={`flex flex-col items-center py-2 px-1 ${isActive ? colors.bottomActive : colors.bottomInactive}`}>
                  <Icon className="h-5 w-5" />
                  <span className="text-[10px] mt-1 font-medium whitespace-nowrap">{item.shortLabel}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden lg:block fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_fieldmaster-24/artifacts/iuxgdyho_Spaces-Logo.png" 
                alt="Spaces by TRP Sports" 
                className="h-10"
              />
              {facility?.logo_url && (
                <div className="h-10 px-2 flex items-center justify-center bg-gray-900 rounded-lg overflow-hidden">
                  <img 
                    src={facility.logo_url} 
                    alt={facility.name} 
                    className="h-8 object-contain"
                    onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2">Admin Panel</p>
          </div>

          {/* Facility Info */}
          {facility && (
            <div className={`p-3 mx-3 mt-3 rounded-lg ${colors.activeBg} ${colors.border} border`}>
              <div className="flex items-center space-x-2">
                <Building2 className={`h-4 w-4 ${colors.text}`} />
                <span className={`text-sm font-semibold ${colors.text}`}>{facility.name}</span>
              </div>
              {facility.sports && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {facility.sports.map(sport => (
                    <span key={sport} className="text-xs bg-white px-2 py-0.5 rounded capitalize">
                      {sport}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* User Info */}
          <div className="p-4 border-b mt-2">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center`}>
                <span className={`${colors.text} font-semibold`}>{user?.name?.charAt(0)}</span>
              </div>
              <div>
                <p className="font-semibold text-sm">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            {/* Push Notifications Setup */}
            <div className="mt-3">
              <NotificationSetup />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} data-testid={`nav-${item.path.split('/').pop()}`}>
                  <div
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? `${colors.activeBg} ${colors.text}`
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
